import React, { useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Camera, History, Loader2 } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { hasOwnerAccess } from '../../lib/owner'
import { useMomentsToday } from '../../hooks/useMomentsToday'
import { supabase } from '../../lib/supabaseClient'
import MomentsReveal from '../../components/MomentsReveal'

// MomentsPage — /moments
//
// The module home. Shows today's prompt status (waiting / open / mine
// submitted / revealed / missed), a current-streak number, and a
// stub for the upcoming history grid. The dashboard card on `/` in
// PR C will be the everyday entry point; this page is for everything
// that doesn't fit on a card.
export function MomentsPage() {
  const { t, i18n } = useTranslation()
  const locale = i18n.language === 'ar' ? 'ar' : 'en'
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const {
    prompt, mine, partner, partnerName, myImageUrl, partnerImageUrl,
    windowOpen, msUntilClose, loading: hookLoading,
  } = useMomentsToday(null, { locale })
  const aiCaption = prompt?.ai_caption || null
  const aiCaptionAr = prompt?.ai_caption_ar || null
  const aiTags = prompt?.ai_tags || null

  const [streak, setStreak] = useState(null)

  useEffect(() => {
    if (!user) return undefined
    let alive = true
    ;(async () => {
      const { data } = await supabase.rpc('moments_current_streak')
      if (alive && typeof data === 'number') setStreak(data)
    })()
    return () => { alive = false }
  }, [user, prompt?.status, mine?.id, partner?.id])

  if (!authLoading && user && !hasOwnerAccess(user)) {
    return <Navigate to="/" replace />
  }
  if (authLoading || hookLoading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin opacity-60" />
      </div>
    )
  }

  const todayKsa = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Riyadh' })
  const isToday = !!prompt && prompt.prompt_date === todayKsa
  const minutesLeft = Math.max(0, Math.ceil(msUntilClose / 60000))

  return (
    <div className="mx-auto max-w-md space-y-5 p-4">
      <div className="flex items-end justify-between">
        <h1 dir="auto" className="text-2xl font-semibold">{t('moments.title')}</h1>
        {streak !== null && (
          <div className="text-end">
            <div className="text-3xl font-bold leading-none">{streak}</div>
            <div className="text-[11px] uppercase tracking-wide opacity-60">{t('moments.streakLabel')}</div>
          </div>
        )}
      </div>

      <p dir="auto" className="text-sm opacity-75">{t('moments.rules')}</p>

      {isToday && windowOpen && !mine && (
        <>
          <button
            type="button"
            onClick={() => navigate(`/moments/capture/${prompt.id}`)}
            className="flex w-full items-center justify-between rounded-2xl bg-rose-500 px-5 py-4 text-white"
          >
            <span dir="auto" className="text-base font-medium">{t('moments.openCamera')}</span>
            <Camera className="h-5 w-5" />
          </button>
          {minutesLeft > 0 && (
            <p dir="auto" className="text-center text-xs opacity-60">
              {t('moments.windowClosingIn', { minutes: minutesLeft })}
            </p>
          )}
        </>
      )}

      {isToday && mine && (
        <MomentsReveal
          mine={mine}
          partner={partner}
          myImageUrl={myImageUrl}
          partnerImageUrl={partnerImageUrl}
          partnerName={partnerName}
          windowOpen={windowOpen}
          viewerId={user?.id}
          locale={locale}
          aiCaption={aiCaption}
          aiCaptionAr={aiCaptionAr}
          aiTags={aiTags}
        />
      )}

      {isToday && !windowOpen && !mine && (
        <p dir="auto" className="text-center text-sm opacity-70">{t('moments.youMissed')}</p>
      )}

      {!isToday && (
        <p dir="auto" className="text-center text-sm opacity-70">{t('moments.noPromptToday')}</p>
      )}

      <button
        type="button"
        onClick={() => navigate('/moments/archive')}
        className="flex w-full items-center justify-between rounded-2xl border border-neutral-200 px-4 py-3 text-sm dark:border-neutral-800"
      >
        <span dir="auto">{t('moments.browseHistory')}</span>
        <History className="h-4 w-4 opacity-60" />
      </button>
    </div>
  )
}
